/**
 * Plane geometry for plans and drawings — points, segments, polygons and
 * axis-aligned bounds. Pure functions in world units (whatever the caller
 * draws in, e.g. metres), y pointing down like the screen: an angle of 90°
 * points down, angles grow clockwise on screen.
 */

export interface NxPlanPoint {
  x: number;
  y: number;
}

/** Axis-aligned rectangle in world units, `min` ≤ `max` on both axes. */
export interface NxPlanBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

const EPSILON = 1e-12;

/** Straight-line distance between two points. */
export function pointDistance(a: NxPlanPoint, b: NxPlanPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Length of the segment `a`–`b`. */
export function segmentLength(a: NxPlanPoint, b: NxPlanPoint): number {
  return pointDistance(a, b);
}

/**
 * Direction of the segment `a` → `b` in degrees, normalised to `[0, 360)`.
 * 0° points right, 90° down (y-down plane). A zero-length segment is 0°.
 */
export function segmentAngle(a: NxPlanPoint, b: NxPlanPoint): number {
  if (Math.abs(b.x - a.x) < EPSILON && Math.abs(b.y - a.y) < EPSILON) return 0;
  return normalizeAngle((Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI);
}

/** Puts an angle in degrees into `[0, 360)`. */
export function normalizeAngle(degrees: number): number {
  const a = degrees % 360;
  const n = a < 0 ? a + 360 : a;
  // -0 and 360 residue from float rounding both read as 0.
  return n >= 360 - 1e-9 || Object.is(n, -0) ? 0 : n;
}

/** The point `length` away from `origin` in direction `degrees`. */
export function pointAtAngle(origin: NxPlanPoint, degrees: number, length: number): NxPlanPoint {
  const rad = (degrees * Math.PI) / 180;
  return { x: origin.x + Math.cos(rad) * length, y: origin.y + Math.sin(rad) * length };
}

/** Middle of the segment `a`–`b`. */
export function segmentMidpoint(a: NxPlanPoint, b: NxPlanPoint): NxPlanPoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export interface NxPlanProjection {
  /** The nearest point on the segment. */
  point: NxPlanPoint;
  /** Position along the segment: 0 = `a`, 1 = `b` (clamped). */
  t: number;
  /** Distance from the input point to `point`. */
  distance: number;
}

/**
 * Projects `point` onto the segment `a`–`b`: the nearest point on it, where
 * it lies along the segment and how far away it is. A zero-length segment
 * projects everything onto `a`.
 */
export function projectOntoSegment(
  point: NxPlanPoint,
  a: NxPlanPoint,
  b: NxPlanPoint
): NxPlanProjection {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  let t = 0;
  if (lengthSq > EPSILON) {
    t = ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSq;
    t = Math.min(1, Math.max(0, t));
  }
  const projected = { x: a.x + dx * t, y: a.y + dy * t };
  return { point: projected, t, distance: pointDistance(point, projected) };
}

/** Shortest distance from `point` to the segment `a`–`b`. */
export function distanceToSegment(point: NxPlanPoint, a: NxPlanPoint, b: NxPlanPoint): number {
  return projectOntoSegment(point, a, b).distance;
}

function cross(o: NxPlanPoint, a: NxPlanPoint, b: NxPlanPoint): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

function onSegment(p: NxPlanPoint, a: NxPlanPoint, b: NxPlanPoint): boolean {
  return (
    Math.min(a.x, b.x) - EPSILON <= p.x &&
    p.x <= Math.max(a.x, b.x) + EPSILON &&
    Math.min(a.y, b.y) - EPSILON <= p.y &&
    p.y <= Math.max(a.y, b.y) + EPSILON
  );
}

/** Whether the segments `a`–`b` and `c`–`d` touch or cross. */
export function segmentsIntersect(
  a: NxPlanPoint,
  b: NxPlanPoint,
  c: NxPlanPoint,
  d: NxPlanPoint
): boolean {
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  if (
    ((d1 > EPSILON && d2 < -EPSILON) || (d1 < -EPSILON && d2 > EPSILON)) &&
    ((d3 > EPSILON && d4 < -EPSILON) || (d3 < -EPSILON && d4 > EPSILON))
  ) {
    return true;
  }
  if (Math.abs(d1) <= EPSILON && onSegment(a, c, d)) return true;
  if (Math.abs(d2) <= EPSILON && onSegment(b, c, d)) return true;
  if (Math.abs(d3) <= EPSILON && onSegment(c, a, b)) return true;
  if (Math.abs(d4) <= EPSILON && onSegment(d, a, b)) return true;
  return false;
}

/**
 * Signed area of a polygon (shoelace). Positive when the points run
 * clockwise on screen (y-down), negative counter-clockwise. The polygon is
 * closed implicitly; fewer than three points have no area.
 */
export function polygonSignedArea(points: readonly NxPlanPoint[]): number {
  if (points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const q = points[(i + 1) % points.length]!;
    sum += p.x * q.y - q.x * p.y;
  }
  return sum / 2;
}

/** Area of a simple polygon, always ≥ 0 (square world units, e.g. m²). */
export function polygonArea(points: readonly NxPlanPoint[]): number {
  return Math.abs(polygonSignedArea(points));
}

/** Perimeter of a closed polygon (the edge back to the first point counts). */
export function polygonPerimeter(points: readonly NxPlanPoint[]): number {
  if (points.length < 2) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    sum += pointDistance(points[i]!, points[(i + 1) % points.length]!);
  }
  return points.length === 2 ? sum / 2 : sum;
}

/**
 * Centre of mass of a simple polygon — where a room label goes. A polygon
 * without area (collinear points, fewer than three) falls back to the mean of
 * its points; an empty list gives `null`.
 */
export function polygonCentroid(points: readonly NxPlanPoint[]): NxPlanPoint | null {
  if (!points.length) return null;
  const area = polygonSignedArea(points);
  if (Math.abs(area) < EPSILON) {
    const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    return { x: sum.x / points.length, y: sum.y / points.length };
  }
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const q = points[(i + 1) % points.length]!;
    const f = p.x * q.y - q.x * p.y;
    cx += (p.x + q.x) * f;
    cy += (p.y + q.y) * f;
  }
  return { x: cx / (6 * area), y: cy / (6 * area) };
}

/**
 * Whether `point` lies inside the polygon (even-odd rule, so it also works
 * for self-touching outlines). A point exactly on an edge counts as inside.
 */
export function pointInPolygon(point: NxPlanPoint, polygon: readonly NxPlanPoint[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    if (distanceToSegment(point, a, b) <= 1e-9) return true;
    if (a.y > point.y !== b.y > point.y) {
      const x = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
      if (point.x < x) inside = !inside;
    }
  }
  return inside;
}

/** Bounds around `points`; `null` for an empty list. */
export function boundsFromPoints(points: readonly NxPlanPoint[]): NxPlanBounds | null {
  if (!points.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/** The rectangle spanned by two corners in any order (a marquee drag). */
export function boundsFromCorners(a: NxPlanPoint, b: NxPlanPoint): NxPlanBounds {
  return {
    minX: Math.min(a.x, b.x),
    minY: Math.min(a.y, b.y),
    maxX: Math.max(a.x, b.x),
    maxY: Math.max(a.y, b.y),
  };
}

/** The smallest bounds around all given bounds (`null`s are skipped). */
export function unionBounds(
  ...bounds: readonly (NxPlanBounds | null | undefined)[]
): NxPlanBounds | null {
  let out: NxPlanBounds | null = null;
  for (const b of bounds) {
    if (!b) continue;
    out = out
      ? {
          minX: Math.min(out.minX, b.minX),
          minY: Math.min(out.minY, b.minY),
          maxX: Math.max(out.maxX, b.maxX),
          maxY: Math.max(out.maxY, b.maxY),
        }
      : { ...b };
  }
  return out;
}

/** Grows (or with a negative margin shrinks) bounds on every side. */
export function expandBounds(bounds: NxPlanBounds, margin: number): NxPlanBounds {
  return {
    minX: bounds.minX - margin,
    minY: bounds.minY - margin,
    maxX: bounds.maxX + margin,
    maxY: bounds.maxY + margin,
  };
}

/** Whether `point` lies inside or on the edge of `bounds`. */
export function boundsContainsPoint(bounds: NxPlanBounds, point: NxPlanPoint): boolean {
  return (
    point.x >= bounds.minX &&
    point.x <= bounds.maxX &&
    point.y >= bounds.minY &&
    point.y <= bounds.maxY
  );
}

/** Whether two bounds overlap or touch. */
export function boundsIntersect(a: NxPlanBounds, b: NxPlanBounds): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

/** The four corners of `bounds`, clockwise from the top left. */
export function boundsCorners(bounds: NxPlanBounds): NxPlanPoint[] {
  return [
    { x: bounds.minX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.minY },
    { x: bounds.maxX, y: bounds.maxY },
    { x: bounds.minX, y: bounds.maxY },
  ];
}

/** Whether the segment `a`–`b` touches `bounds` (an end inside or an edge crossed). */
export function segmentIntersectsBounds(
  a: NxPlanPoint,
  b: NxPlanPoint,
  bounds: NxPlanBounds
): boolean {
  if (boundsContainsPoint(bounds, a) || boundsContainsPoint(bounds, b)) return true;
  if (!boundsIntersect(boundsFromCorners(a, b), bounds)) return false;
  const c = boundsCorners(bounds);
  for (let i = 0; i < 4; i++) {
    if (segmentsIntersect(a, b, c[i]!, c[(i + 1) % 4]!)) return true;
  }
  return false;
}

/**
 * Pushes points apart until no two are closer than `minDistance`, moving
 * each as little as it can — markers that would cover each other, two
 * sensors side by side on one wall. Points on the same spot fan out around
 * it. Works in any unit, usually screen pixels (spread after projecting, so
 * markers separate only while they would overlap). Answers new points in the
 * same order.
 */
export function spreadPoints(
  points: readonly NxPlanPoint[],
  minDistance: number,
  iterations = 8
): NxPlanPoint[] {
  const out = points.map((p) => ({ x: p.x, y: p.y }));
  if (!(minDistance > 0)) return out;
  for (let pass = 0; pass < iterations; pass++) {
    let moved = false;
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i]!;
        const b = out[j]!;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        const distance = Math.hypot(dx, dy);
        if (distance >= minDistance - EPSILON) continue;
        if (distance < EPSILON) {
          // The golden angle keeps a stack of equal points from lining up.
          const angle = (j * 2.399963229728653) % (2 * Math.PI);
          dx = Math.cos(angle);
          dy = Math.sin(angle);
        } else {
          dx /= distance;
          dy /= distance;
        }
        const push = (minDistance - distance) / 2;
        a.x -= dx * push;
        a.y -= dy * push;
        b.x += dx * push;
        b.y += dy * push;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return out;
}
